/**
 * src/components/mfa/MfaNudgeBanner.tsx
 * 追加認証が未設定のユーザーへの控えめな案内（操作は止めない）。
 *   - 利用できる方式がサーバーで有効なときだけ表示（未準備の環境では出さない）
 *   - ×で今回のログイン中は非表示
 *   - 管理者でパスキーが1つだけの場合は「予備の端末の登録」を案内
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../AuthGuard'
import { useIsMobile } from '../../hooks/useIsMobile'

const KEY = 'mangaos-mfa-nudge-hidden'

export function MfaNudgeBanner() {
  const { user, isAdmin } = useAuth()
  const isMobile = useIsMobile()
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(KEY) === '1' } catch { return false }
  })

  if (!user || hidden || user.mfa_setup_required) return null
  const opts = user.mfa_options
  if (!opts) return null

  let title: string | null = null
  let body = ''
  if (user.mfa_method === 'none') {
    if (isAdmin && opts.passkey) {
      title = '🛡️ 管理者のパスキー登録をおすすめします'
      body = 'パスワードに加えて端末の認証（Windows Hello・指紋/顔認証など）でログインできるようになり、安全性が大きく上がります。'
    } else if (!isAdmin && (opts.passkey || opts.email)) {
      title = '🛡️ ログインの追加認証を設定できます'
      body = opts.passkey
        ? 'パスキー（顔認証・指紋など）またはメールの6桁コードで、アカウントをより安全に守れます。'
        : 'メールに届く6桁のコードで、アカウントをより安全に守れます。'
    }
  } else if (isAdmin && user.mfa_method === 'passkey' && (user.passkey_count ?? 0) === 1) {
    title = '🛡️ 予備のパスキーを登録しておきましょう'
    body = '端末の故障・紛失に備えて、別の端末（スマートフォンやセキュリティキー）のパスキーも登録してください。'
  }
  if (!title) return null

  const dismiss = () => {
    try { sessionStorage.setItem(KEY, '1') } catch { /* ignore */ }
    setHidden(true)
  }

  return (
    <div style={{
      background: 'var(--blue-light)', borderBottom: '1px solid var(--paper-3)',
      padding: isMobile ? '10px 14px' : '7px 16px', display: 'flex',
      flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center',
      gap: isMobile ? 8 : 12, flexWrap: isMobile ? 'nowrap' : 'wrap', fontSize: 12, boxSizing: 'border-box', width: '100%',
    }}>
      <span style={{ flex: isMobile ? 'none' : 1, minWidth: isMobile ? 0 : 200, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        <strong style={{ marginRight: 6, display: isMobile ? 'block' : 'inline' }}>{title}</strong>
        <span style={{ color: 'var(--ink-3)' }}>{body}</span>
      </span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
        <Link to="/account/security" className="btn btn-sm btn-outline" style={{ fontSize: 11, whiteSpace: 'nowrap', padding: '3px 10px' }}>
          ログイン・セキュリティを開く
        </Link>
        <button onClick={dismiss} aria-label="案内を閉じる" title="今回のログイン中は非表示"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>
          ×
        </button>
      </div>
    </div>
  )
}
